type UnknownRecord = Record<string, unknown>;

export type LivePrintComExecution = {
  execution: string;
  sku: string;
  options: UnknownRecord;
  quantity: number;
};

export function buildLivePrintComBatchRequest(
  resolved: LivePrintComExecution,
  countryCode: string,
  locale: string,
) {
  return {
    pricingMode: 'api' as const,
    expandPrintingMethods: false,
    batch: [{
      sku: resolved.sku,
      quantity: resolved.quantity,
      options: resolved.options,
      countryCode,
      locale,
      strictOptions: true,
    }],
  };
}

function asRecord(value: unknown): UnknownRecord {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as UnknownRecord
    : {};
}

export function getPricingConfigurationOptions(configuration: unknown): UnknownRecord {
  const root = asRecord(configuration);
  const nested = asRecord(root.options);
  return Object.keys(nested).length > 0 ? nested : root;
}

/**
 * A CUSTOM product can contain one or more executions whose catalogue lives
 * locally, while the price must always come from Print.com. Only the explicitly
 * listed customer choices and fixed provider options are forwarded; internal
 * merge fields such as material_family and execution never reach Print.com.
 */
export function resolveLivePrintComExecution(
  specifications: unknown,
  configuration: unknown,
  requestedQuantity = 1,
): LivePrintComExecution | null {
  const specs = asRecord(specifications);
  const settings = asRecord(specs.livePrintComPricing);
  const executions = Array.isArray(settings.executions)
    ? settings.executions.map(String)
    : [];
  const selected = getPricingConfigurationOptions(configuration);
  const execution = String(selected.execution || '').trim();
  const sku = String(settings.sku || '').trim();

  if (!sku || !execution || !executions.includes(execution)) return null;

  const fixedOptions = asRecord(settings.fixedOptions);
  const configurationKeys = Array.isArray(settings.configurationKeys)
    ? settings.configurationKeys.map(String)
    : ['size', 'copies'];
  const options: UnknownRecord = { ...fixedOptions };

  for (const key of configurationKeys) {
    const value = selected[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      options[key] = value;
    }
  }

  const hasCopiesOption = options.copies !== undefined;
  return {
    execution,
    sku,
    options,
    quantity: hasCopiesOption ? 1 : Math.max(1, Number(requestedQuantity) || 1),
  };
}
